import {
  EVIDENCE_FILE_HASH_CONFLICT_CODE,
  EVIDENCE_FILE_LIST_GROUP_BY_VALUES,
  EVIDENCE_FILE_LIST_SEARCH_MAX_LENGTH,
  EVIDENCE_FILE_LIST_SORT_BY_VALUES,
  EVIDENCE_FILE_LIST_SORT_ORDER_VALUES,
} from '../../features/evidence-files/evidence-files.models';
import { findCaseById } from '../mock-data/cases.mock';
import { findTenantDocumentTypeByPublicId } from '../mock-data/document-types.mock';
import {
  findEvidenceFilesByProjectId,
  findEvidenceFileById,
  findEvidenceFilesByCaseId,
  findEvidenceFileByNameAndHash,
  deleteEvidenceFile,
  addEvidenceFile,
  updateEvidenceFileAttribution,
} from '../mock-data/evidence-files.mock';
import {
  createMockResponse,
  createMockErrorResponse,
} from '../mock.interceptor';

import type {
  AttributionUpdateApiRequest,
  AttributionValidationError,
  CaseEvidenceFilesListApiResponse,
  EvidenceFileApiResponse,
  EvidenceFileAttributionApiResponse,
  EvidenceFileListGroupBy,
  EvidenceFileListSortBy,
  EvidenceFileListSortOrder,
  FileProcessingStatusApiResponse,
  ProjectEvidenceFileItemApiResponse,
  ProjectEvidenceFilesListApiResponse,
  UploadUrlApiRequest,
  UploadUrlApiResponse,
} from '../../features/evidence-files/evidence-files.models';
import type { AttributionUnit } from '../../features/evidence-files/required-documents.models';
import type { HttpResponse } from '@angular/common/http';
import type { Observable } from 'rxjs';

/**
 * /projects/:projectId/evidence-files* へのリクエストをハンドリング
 * path はテナントプレフィックス除去済みの短縮パス
 */
export function handleEvidenceFilesRequest(
  method: string,
  path: string,
  body: unknown,
  searchParams: URLSearchParams,
): Observable<HttpResponse<unknown>> | null {
  // POST /projects/:projectId/evidence-files/:evidenceFileId/replace-url - 差し替えURL取得
  // pdf/download-url より先にマッチさせる（正規表現の順序に注意）
  const replaceUrlMatch =
    /^\/projects\/([^/]+)\/evidence-files\/([^/]+)\/replace-url$/.exec(path);
  if (method === 'POST' && replaceUrlMatch) {
    const projectId = replaceUrlMatch[1];
    const evidenceFileId = replaceUrlMatch[2];
    return handleGetReplaceUrl(
      projectId,
      evidenceFileId,
      body as UploadUrlApiRequest,
    );
  }

  // POST /projects/:projectId/evidence-files/:evidenceFileId/pdf/download-url - PDF download URL取得
  const pdfDownloadUrlMatch =
    /^\/projects\/([^/]+)\/evidence-files\/([^/]+)\/pdf\/download-url$/.exec(
      path,
    );
  if (method === 'POST' && pdfDownloadUrlMatch) {
    const projectId = pdfDownloadUrlMatch[1];
    const evidenceFileId = pdfDownloadUrlMatch[2];
    return handleGetPdfDownloadUrl(projectId, evidenceFileId);
  }

  // POST /projects/:projectId/evidence-files/upload-url - アップロードURL取得
  const uploadUrlMatch =
    /^\/projects\/([^/]+)\/evidence-files\/upload-url$/.exec(path);
  if (method === 'POST' && uploadUrlMatch) {
    const projectId = uploadUrlMatch[1];
    return handleGetUploadUrl(projectId, body as UploadUrlApiRequest);
  }

  // PATCH /projects/:projectId/evidence-files/:evidenceFileId - attribution 訂正 (Issue #443/#447)
  const patchFileMatch = /^\/projects\/([^/]+)\/evidence-files\/([^/]+)$/.exec(
    path,
  );
  if (method === 'PATCH' && patchFileMatch) {
    const projectId = patchFileMatch[1];
    const evidenceFileId = patchFileMatch[2];
    return handleUpdateEvidenceFileAttribution(
      projectId,
      evidenceFileId,
      body as AttributionUpdateApiRequest,
    );
  }

  // DELETE /projects/:projectId/evidence-files/:evidenceFileId - ファイル削除
  const deleteFileMatch = /^\/projects\/([^/]+)\/evidence-files\/([^/]+)$/.exec(
    path,
  );
  if (method === 'DELETE' && deleteFileMatch) {
    const projectId = deleteFileMatch[1];
    const evidenceFileId = deleteFileMatch[2];
    return handleDeleteEvidenceFile(projectId, evidenceFileId);
  }

  // GET /projects/:projectId/cases/:caseId/evidence-files - 審査対象配下ファイル一覧取得
  const getCaseFilesMatch =
    /^\/projects\/([^/]+)\/cases\/([^/]+)\/evidence-files$/.exec(path);
  if (method === 'GET' && getCaseFilesMatch) {
    const projectId = getCaseFilesMatch[1];
    const caseId = getCaseFilesMatch[2];
    return handleGetCaseEvidenceFiles(projectId, caseId, searchParams);
  }

  // GET /projects/:projectId/evidence-files - ファイル一覧取得
  const getFilesMatch = /^\/projects\/([^/]+)\/evidence-files$/.exec(path);
  if (method === 'GET' && getFilesMatch) {
    const projectId = getFilesMatch[1];
    return handleGetEvidenceFiles(projectId, searchParams);
  }

  return null;
}

/**
 * GET /api/v0/tenants/:tenantId/projects/:projectId/evidence-files - ファイル一覧取得
 * Issue #453: sortBy / sortOrder / groupBy / search / cursor / limit に対応。
 */
function handleGetEvidenceFiles(
  projectId: string,
  searchParams: URLSearchParams,
): Observable<HttpResponse<ProjectEvidenceFilesListApiResponse | ProblemBody>> {
  const parsed = parseEvidenceFileListQuery(searchParams);
  if (!parsed.ok) return parsed.response;

  const page = buildEvidenceFileListPage(
    projectId,
    findEvidenceFilesByProjectId(projectId),
    parsed.query,
  );
  if (!page.ok) return page.response;

  return createMockResponse<ProjectEvidenceFilesListApiResponse>({
    projectId,
    items: page.items,
    nextCursor: page.nextCursor,
    totalCount: page.totalCount,
  });
}

/**
 * GET /api/v0/tenants/:tenantId/projects/:projectId/cases/:caseId/evidence-files - 審査対象配下ファイル一覧取得
 * Issue #453: project 版と同じソート / グルーピング / 検索クエリに対応。
 */
function handleGetCaseEvidenceFiles(
  projectId: string,
  caseId: string,
  searchParams: URLSearchParams,
): Observable<HttpResponse<CaseEvidenceFilesListApiResponse | ProblemBody>> {
  const parsed = parseEvidenceFileListQuery(searchParams);
  if (!parsed.ok) return parsed.response;

  const page = buildEvidenceFileListPage(
    projectId,
    findEvidenceFilesByCaseId(projectId, caseId),
    parsed.query,
  );
  if (!page.ok) return page.response;

  return createMockResponse<CaseEvidenceFilesListApiResponse>({
    projectId,
    caseId,
    items: page.items,
    nextCursor: page.nextCursor,
    totalCount: page.totalCount,
  });
}

// =============================================================================
// Issue #453: 一覧のソート / グルーピング / 検索 / cursor ページング
//
// 仕様の正は docs/contracts/api/api-server.openapi.json（経緯は docs/archive/new-domain/api-proposal/CHANGES-api-server.md の 10 節）。
// mock は実 DB を持たないため配列上で同じ観測可能な振る舞いを再現する:
//   - 実行順は [groupBy 列(常に昇順)] → [sortBy 列(sortOrder)] → タイブレーカー
//     （サーバは id、mock は元配列の index）
//   - 文字列の比較はコードポイント順（{@link compareStringsByCodePoint}）
//   - nullable なソートキーは asc で末尾 / desc で先頭
//   - search はファイル名の部分一致（大文字小文字を区別しない）。substring 比較のため
//     ILIKE の `%` `_` は自動的にリテラル扱いになる
//   - totalCount は search 適用後の件数
// =============================================================================

/** limit の既定値・範囲（api-server の EvidenceFileListQuerySchema と同値） */
const DEFAULT_LIST_LIMIT = 100;
const MIN_LIST_LIMIT = 1;
const MAX_LIST_LIMIT = 500;

/** PostgreSQL enum の定義順（サーバは enum 定義順で整列するため index に写す） */
const UPLOAD_STATUS_ORDER = ['pending', 'uploaded', 'error'] as const;
const OCR_STATUS_ORDER = [
  'pending',
  'processing',
  'completed',
  'failed',
] as const;
const CONVERT_STATUS_ORDER = [
  'idle',
  'awaiting_conversion',
  'downloading',
  'completed',
  'failed',
] as const;

/** 解釈済みの一覧クエリ */
type ParsedListQuery = {
  sortBy: EvidenceFileListSortBy | null;
  sortOrder: EvidenceFileListSortOrder;
  groupBy: EvidenceFileListGroupBy | null;
  search: string;
  limit: number;
  cursor: string | null;
};

/** cursor に格納するペイロード（base64url(JSON)） */
type CursorPayload = {
  /** 次ページ先頭の offset */
  o: number;
  /** 条件シグネチャ（groupBy|sortBy|sortOrder|search） */
  sig: string;
};

/**
 * ProblemDetail (RFC 9457) 形式の body 型。
 * 一覧クエリ 400 と PATCH attribution のエラーレスポンスで共用する。
 */
type ProblemBody = {
  title: string;
  status: number;
  detail: string;
  instance?: string;
};

/**
 * 一覧クエリ 400 応答の共通型。
 * api-server は application/problem+json（ProblemDetail）を返すため body 形状を揃える。
 */
type ListQueryErrorResponse = Observable<HttpResponse<ProblemBody>>;

/**
 * 一覧クエリ不正の 400 を ProblemDetail 形式で返す（CHANGES-api-server.md 10-7）。
 * PATCH attribution 側と同じく api-server の `mapErrorToHttpResponse` の形状に合わせる。
 */
function createListQueryErrorResponse(detail: string): ListQueryErrorResponse {
  return createMockResponse<ProblemBody>(
    { title: 'Bad Request', status: 400, detail },
    400,
  );
}

/** UTF-8 安全な base64url エンコード（search に日本語が入るため） */
function encodeBase64Url(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/** UTF-8 安全な base64url デコード（不正入力は null） */
function decodeBase64Url(value: string): string | null {
  try {
    const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
    const padding =
      base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4));
    const binary = atob(base64 + padding);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}

/** 条件シグネチャ。cursor と現在の条件の不一致検出に使う */
function buildConditionSignature(query: ParsedListQuery): string {
  return [
    query.groupBy ?? '',
    query.sortBy ?? '',
    query.sortOrder,
    query.search,
  ].join('|');
}

/**
 * クエリパラメータを解釈する。
 * enum 外の sortBy / sortOrder / groupBy、長すぎる search、範囲外の limit は 400。
 */
function parseEvidenceFileListQuery(
  searchParams: URLSearchParams,
):
  | { ok: true; query: ParsedListQuery }
  | { ok: false; response: ListQueryErrorResponse } {
  const rawSortBy = searchParams.get('sortBy');
  if (
    rawSortBy !== null &&
    !EVIDENCE_FILE_LIST_SORT_BY_VALUES.includes(
      rawSortBy as EvidenceFileListSortBy,
    )
  ) {
    return {
      ok: false,
      response: createListQueryErrorResponse(`sortBy が不正です: ${rawSortBy}`),
    };
  }

  const rawSortOrder = searchParams.get('sortOrder');
  if (
    rawSortOrder !== null &&
    !EVIDENCE_FILE_LIST_SORT_ORDER_VALUES.includes(
      rawSortOrder as EvidenceFileListSortOrder,
    )
  ) {
    return {
      ok: false,
      response: createListQueryErrorResponse(
        `sortOrder が不正です: ${rawSortOrder}`,
      ),
    };
  }

  const rawGroupBy = searchParams.get('groupBy');
  if (
    rawGroupBy !== null &&
    !EVIDENCE_FILE_LIST_GROUP_BY_VALUES.includes(
      rawGroupBy as EvidenceFileListGroupBy,
    )
  ) {
    return {
      ok: false,
      response: createListQueryErrorResponse(
        `groupBy が不正です: ${rawGroupBy}`,
      ),
    };
  }

  const search = (searchParams.get('search') ?? '').trim();
  if (search.length > EVIDENCE_FILE_LIST_SEARCH_MAX_LENGTH) {
    return {
      ok: false,
      response: createListQueryErrorResponse(
        `search は ${EVIDENCE_FILE_LIST_SEARCH_MAX_LENGTH} 文字以内で指定してください`,
      ),
    };
  }

  // limit は openapi / api-server の EvidenceFileListQuerySchema と同じく
  // 整数かつ 1..500 の範囲外を 400 にする（clamp しない）。
  const rawLimit = searchParams.get('limit');
  const parsedLimit =
    rawLimit === null ? DEFAULT_LIST_LIMIT : Number.parseInt(rawLimit, 10);
  if (
    !Number.isInteger(parsedLimit) ||
    parsedLimit < MIN_LIST_LIMIT ||
    parsedLimit > MAX_LIST_LIMIT
  ) {
    return {
      ok: false,
      response: createListQueryErrorResponse(
        `limit は ${MIN_LIST_LIMIT} 以上 ${MAX_LIST_LIMIT} 以下の整数で指定してください: ${rawLimit}`,
      ),
    };
  }
  const limit = parsedLimit;

  return {
    ok: true,
    query: {
      sortBy: rawSortBy as EvidenceFileListSortBy | null,
      sortOrder: (rawSortOrder ?? 'asc') as EvidenceFileListSortOrder,
      groupBy: rawGroupBy as EvidenceFileListGroupBy | null,
      search,
      limit,
      cursor: searchParams.get('cursor'),
    },
  };
}

/** ソートキー値。null は「未分類 / 未変換 / project 帰属」を表す */
type SortKeyValue = string | number | null;

/**
 * documentType 列（サーバは document_types.display_name で整列）。
 *
 * サーバは document_type_id の FK join なので lookup 失敗はあり得ず、未割当なら
 * display_name が NULL になる。mock も lookup 失敗時は documentTypeCode へ
 * フォールバックせず null（= 未分類扱い）に寄せる。
 */
function documentTypeSortKey(
  item: FileProcessingStatusApiResponse,
): SortKeyValue {
  const { documentTypePublicId } = item.evidenceFile.attribution;
  if (documentTypePublicId === null) return null;
  return findTenantDocumentTypeByPublicId(documentTypePublicId)?.name ?? null;
}

/**
 * attributionTarget 列（サーバは COALESCE(cases.item_key, employee_id, target_month)）。
 *
 * documentTypeSortKey と同じ理由で、case_id の join 失敗時に生の caseId へ
 * フォールバックせず null に寄せる（サーバに対応する挙動が存在しないため）。
 */
function attributionTargetSortKey(
  projectId: string,
  item: FileProcessingStatusApiResponse,
): SortKeyValue {
  const { caseId, employeeId, targetMonth } = item.evidenceFile.attribution;
  if (caseId !== null) {
    return findCaseById(projectId, caseId)?.itemKey ?? null;
  }
  return employeeId ?? targetMonth;
}

/** enum 値を定義順の index に写す（未知値は null） */
function enumOrder(values: readonly string[], value: string): SortKeyValue {
  const index = values.indexOf(value);
  return index === -1 ? null : index;
}

/** sortBy 列のソートキーを取り出す（sortBy 省略時はサーバ既定の createdAt） */
function evidenceFileSortKey(
  projectId: string,
  item: FileProcessingStatusApiResponse,
  sortBy: EvidenceFileListSortBy | null,
): SortKeyValue {
  switch (sortBy) {
    case 'fileName':
      return item.evidenceFile.fileName;
    case 'documentType':
      return documentTypeSortKey(item);
    case 'attributionTarget':
      return attributionTargetSortKey(projectId, item);
    case 'uploadStatus':
      return enumOrder(UPLOAD_STATUS_ORDER, item.evidenceFile.uploadStatus);
    case 'ocrStatus':
      return enumOrder(OCR_STATUS_ORDER, item.evidenceFile.ocrStatus);
    case 'convertStatus':
      // 未変換（convertedArtifact=null）は null 扱い
      return item.convertedArtifact === null
        ? null
        : enumOrder(CONVERT_STATUS_ORDER, item.convertedArtifact.status);
    case 'uploadedAt':
      return Date.parse(item.evidenceFile.uploadedAt);
    case null:
      return Date.parse(item.evidenceFile.createdAt);
    default: {
      // 網羅性チェック（sortBy 追加時に型エラーで気付ける）
      const _exhaustive: never = sortBy;
      return _exhaustive;
    }
  }
}

/** groupBy 列のグループキーを取り出す */
function evidenceFileGroupKey(
  projectId: string,
  item: FileProcessingStatusApiResponse,
  groupBy: EvidenceFileListGroupBy,
): SortKeyValue {
  return groupBy === 'documentType'
    ? documentTypeSortKey(item)
    : attributionTargetSortKey(projectId, item);
}

/**
 * 文字列をコードポイント順で比較する。
 *
 * `localeCompare()` を引数なしで使うと実行環境の既定ロケールに依存し、日本語ロケールでは
 * 漢字が「読み順」で並ぶ（給与明細 → 雇用契約書 → 出勤簿 …）。実サーバの並びは
 * コードポイント順（出勤簿 → 給与明細 → 雇用契約書 …）なので、mock で確認した並びが
 * 実サーバで再現しない。ローカル DB（postgres:16-alpine = musl のバイト順）とも
 * 本番 RDS（glibc の CJK は ISO 14651 の implicit weight = コードポイント）とも
 * 一致するコードポイント比較に固定する。
 */
function compareStringsByCodePoint(a: string, b: string): number {
  if (a < b) return -1;
  return a > b ? 1 : 0;
}

/** null は asc で末尾 / desc で先頭。非 null 同士は数値差 or 文字列比較 */
function compareSortKeys(
  a: SortKeyValue,
  b: SortKeyValue,
  order: EvidenceFileListSortOrder,
): number {
  if (a === null && b === null) return 0;
  if (a === null) return order === 'asc' ? 1 : -1;
  if (b === null) return order === 'asc' ? -1 : 1;

  const result =
    typeof a === 'number' && typeof b === 'number'
      ? a - b
      : compareStringsByCodePoint(String(a), String(b));
  return order === 'asc' ? result : -result;
}

/**
 * ソート / グルーピング / 検索 / cursor ページングを適用して 1 ページ分を組み立てる。
 * 元配列は破壊しない。
 */
function buildEvidenceFileListPage<T extends FileProcessingStatusApiResponse>(
  projectId: string,
  all: T[],
  query: ParsedListQuery,
):
  | { ok: true; items: T[]; nextCursor: string | null; totalCount: number }
  | { ok: false; response: ListQueryErrorResponse } {
  const signature = buildConditionSignature(query);

  // cursor の解釈（デコード不能・条件シグネチャ不一致は 400。生値は応答に反射しない）
  // 空文字は api-server と同じく「cursor 未指定」= 1 ページ目として扱う。
  let offset = 0;
  if (query.cursor !== null && query.cursor !== '') {
    const decoded = decodeCursor(query.cursor);
    if (decoded?.sig !== signature) {
      return {
        ok: false,
        response: createListQueryErrorResponse(
          'cursor が不正、または現在のソート・グルーピング・検索条件と一致しません',
        ),
      };
    }
    offset = decoded.o;
  }

  // search（ファイル名の部分一致、大文字小文字を区別しない）
  const needle = query.search.toLowerCase();
  const filtered =
    needle === ''
      ? [...all]
      : all.filter((item) =>
          item.evidenceFile.fileName.toLowerCase().includes(needle),
        );

  // [groupBy(昇順固定)] → [sortBy(sortOrder)] → 元配列の index（安定タイブレーカー）
  const decorated = filtered.map((item, index) => ({ item, index }));
  decorated.sort((a, b) => {
    if (query.groupBy !== null) {
      const groupDiff = compareSortKeys(
        evidenceFileGroupKey(projectId, a.item, query.groupBy),
        evidenceFileGroupKey(projectId, b.item, query.groupBy),
        'asc',
      );
      if (groupDiff !== 0) return groupDiff;
    }
    const sortDiff = compareSortKeys(
      evidenceFileSortKey(projectId, a.item, query.sortBy),
      evidenceFileSortKey(projectId, b.item, query.sortBy),
      query.sortOrder,
    );
    if (sortDiff !== 0) return sortDiff;
    return a.index - b.index;
  });

  const sorted = decorated.map((entry) => entry.item);
  const items = sorted.slice(offset, offset + query.limit);
  const nextOffset = offset + query.limit;
  const nextCursor =
    nextOffset < sorted.length
      ? encodeBase64Url(
          JSON.stringify({
            o: nextOffset,
            sig: signature,
          } satisfies CursorPayload),
        )
      : null;

  return { ok: true, items, nextCursor, totalCount: sorted.length };
}

/** cursor をデコードして payload に narrow する（不正形状は null） */
function decodeCursor(cursor: string): CursorPayload | null {
  const json = decodeBase64Url(cursor);
  if (json === null) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;

  const record = parsed as Record<string, unknown>;
  const offset = record['o'];
  const signature = record['sig'];
  if (typeof offset !== 'number' || !Number.isInteger(offset) || offset < 0) {
    return null;
  }
  if (typeof signature !== 'string') return null;

  return { o: offset, sig: signature };
}

/**
 * DELETE /api/v0/tenants/:tenantId/projects/:projectId/evidence-files/:evidenceFileId - ファイル削除
 */
function handleDeleteEvidenceFile(
  projectId: string,
  evidenceFileId: string,
): Observable<HttpResponse<{ success: boolean } | { message: string }>> {
  // ファイルの存在確認
  const file = findEvidenceFileById(projectId, evidenceFileId);
  if (!file) {
    return createMockErrorResponse('ファイルが見つかりません', 404);
  }

  // 削除実行
  const deleted = deleteEvidenceFile(projectId, evidenceFileId);
  if (!deleted) {
    return createMockErrorResponse('ファイルの削除に失敗しました', 500);
  }

  return createMockResponse({ success: true });
}

/**
 * POST /api/v0/tenants/:tenantId/projects/:projectId/evidence-files/:evidenceFileId/pdf/download-url - PDF download URL取得
 * api-server.openapi.json の仕様: convertedArtifact が completed 状態の場合のみ URL 発行可能
 */
function handleGetPdfDownloadUrl(
  projectId: string,
  evidenceFileId: string,
): Observable<
  HttpResponse<{ downloadUrl: string; expiresIn: number } | { message: string }>
> {
  // ファイルの存在確認
  const file = findEvidenceFileById(projectId, evidenceFileId);
  if (!file) {
    return createMockErrorResponse('ファイルが見つかりません', 404);
  }

  // convertedArtifact が completed 状態の場合のみ URL 発行可能（api-server.openapi.json の仕様）
  if (file.convertedArtifact?.status !== 'completed') {
    return createMockErrorResponse('ファイルの変換が完了していません', 422);
  }

  // Mock用のローカルサンプルPDF (public ディレクトリに配置)。
  // window.open で新規ウィンドウを開く際、相対パス '/sample.pdf' だと Angular
  // dev-server の SPA fallback が index.html を返し、Router の '**' → '/tenants'
  // redirect にヒットしてしまうため、絶対 URL にして SPA fallback を回避する。
  const downloadUrl = `${window.location.origin}/sample.pdf`;

  return createMockResponse({ downloadUrl, expiresIn: 3600 });
}

/**
 * upload-url の 409 重複応答 body（Issue #396）。
 * ProblemBody に加えて安定判別子 code と enrichment（existingFileId /
 * existingFileName）を RFC 7807 の拡張メンバーとしてトップレベルに持つ
 * （api-server の応答形状に合わせる）。
 */
type DuplicateEvidenceFileProblemBody = ProblemBody & {
  code: typeof EVIDENCE_FILE_HASH_CONFLICT_CODE;
  existingFileId: string;
  existingFileName: string;
};

/**
 * 409 重複を RFC 7807 形式で返す（Issue #396）。
 * detail 文言は api-server の実装をそのまま踏襲する。
 * mock は事前チェック経路のみをシミュレートするため enrichment を必ず載せるが、
 * viewer 側の判定は code のみに依存する（api-server の DB unique backstop 経路は
 * enrichment を載せられないため）。
 */
function createDuplicateEvidenceFileResponse(
  existingFileId: string,
  existingFileName: string,
): Observable<HttpResponse<DuplicateEvidenceFileProblemBody>> {
  return createMockResponse<DuplicateEvidenceFileProblemBody>(
    {
      title: 'Conflict',
      status: 409,
      detail: `Existing EvidenceFile: ${existingFileName} (${existingFileId})`,
      code: EVIDENCE_FILE_HASH_CONFLICT_CODE,
      existingFileId,
      existingFileName,
    },
    409,
  );
}

/**
 * POST /api/v0/tenants/:tenantId/projects/:projectId/evidence-files/upload-url - アップロードURL取得
 * 重複（fileName と fileHash の両方が一致）の場合は RFC 7807 形式の 409 を返す
 */
function handleGetUploadUrl(
  projectId: string,
  request: UploadUrlApiRequest,
): Observable<
  HttpResponse<
    | UploadUrlApiResponse
    | DuplicateEvidenceFileProblemBody
    | { message: string }
  >
> {
  // contentType の存在確認（viewer 先行実装のため防御的チェック）
  if (!request.contentType) {
    return createMockErrorResponse('contentType は必須です', 400);
  }

  // fileName と fileHash の両方が一致する場合のみ重複とみなす（api-server.openapi.json の重複判定条件）
  const existingFile = findEvidenceFileByNameAndHash(
    projectId,
    request.fileName,
    request.fileHash,
  );
  if (existingFile) {
    return createDuplicateEvidenceFileResponse(
      existingFile.evidenceFile.publicId,
      existingFile.evidenceFile.fileName,
    );
  }

  // 新規ファイルIDを生成
  const publicId = `evidence-file-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

  // Mockデータにファイルを追加（新規アップロードは pending 状態）
  const newFile: ProjectEvidenceFileItemApiResponse = {
    evidenceFile: {
      publicId,
      fileHash: request.fileHash,
      fileName: request.fileName,
      uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      uploadedAt: new Date().toISOString(),
      uploadStatus: 'pending',
      ocrStatus: 'pending',
      // OCR 未実行なので失敗理由は持たない（Issue #475 Phase 3）
      ocrFailedReason: null,
      attribution: {
        documentTypeCode: null,
        documentTypePublicId: null,
        attributionUnit: null,
        caseId: null,
        employeeId: null,
        targetMonth: null,
      },
    },
    convertedArtifact: null,
    caseId: null,
  };
  addEvidenceFile(projectId, newFile);

  // Mock用のS3署名付きURL（実際にはアクセスしない）
  const uploadUrl = `https://mock-s3-bucket.s3.ap-northeast-1.amazonaws.com/uploads/${projectId}/${publicId}?X-Amz-Signature=mock`;

  return createMockResponse<UploadUrlApiResponse>({
    publicId,
    fileName: request.fileName,
    uploadUrl,
    expiresIn: 3600,
  });
}

/**
 * POST /api/v0/tenants/:tenantId/projects/:projectId/evidence-files/:evidenceFileId/replace-url - 差し替えURL取得
 * 既存ファイルを削除して新規ファイルIDで再作成する（差し替えのセマンティクス）
 *
 * Issue #396 以降、viewer からこのエンドポイントを叩く経路は存在しない（409 は自動スキップ）。
 * API 仕様のシミュレータとして残置している。
 */
function handleGetReplaceUrl(
  projectId: string,
  evidenceFileId: string,
  request: UploadUrlApiRequest,
): Observable<HttpResponse<UploadUrlApiResponse | { message: string }>> {
  // ファイルの存在確認
  const existingFile = findEvidenceFileById(projectId, evidenceFileId);
  if (!existingFile) {
    return createMockErrorResponse('ファイルが見つかりません', 404);
  }

  // 既存ファイルを削除
  deleteEvidenceFile(projectId, evidenceFileId);

  // 新規ファイルIDを生成して追加（差し替えも pending 状態から開始）
  const newPublicId = `evidence-file-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  const newFile: ProjectEvidenceFileItemApiResponse = {
    evidenceFile: {
      publicId: newPublicId,
      fileHash: request.fileHash,
      fileName: request.fileName,
      uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      uploadedAt: new Date().toISOString(),
      uploadStatus: 'pending',
      ocrStatus: 'pending',
      // OCR 未実行なので失敗理由は持たない（Issue #475 Phase 3）
      ocrFailedReason: null,
      attribution: {
        documentTypeCode: null,
        documentTypePublicId: null,
        attributionUnit: null,
        caseId: null,
        employeeId: null,
        targetMonth: null,
      },
    },
    convertedArtifact: null,
    caseId: existingFile.caseId,
  };
  addEvidenceFile(projectId, newFile);

  // Mock用のS3署名付きURL
  const uploadUrl = `https://mock-s3-bucket.s3.ap-northeast-1.amazonaws.com/uploads/${projectId}/${newPublicId}?X-Amz-Signature=mock`;

  return createMockResponse<UploadUrlApiResponse>({
    publicId: newPublicId,
    fileName: request.fileName,
    uploadUrl,
    expiresIn: 3600,
  });
}

// =============================================================================
// PATCH /evidence-files/:evidenceFileId - attribution 訂正 (Issue #443/#447)
// =============================================================================

/**
 * 400 InvalidAttributionReassignmentError を ProblemDetail 形式で返す。
 * api-server の `mapErrorToHttpResponse` と同じ形状にすることで、viewer の
 * `isAttributionValidationError` (`detail` に埋め込まれた JSON 文字列を parse する
 * ロジック) がそのまま動作する。
 */
function createAttributionValidationResponse(
  field: AttributionValidationError['field'],
  message: string,
): Observable<HttpResponse<ProblemBody>> {
  const problem: ProblemBody = {
    title: 'Bad Request',
    status: 400,
    detail: JSON.stringify({
      type: 'InvalidAttributionReassignmentError',
      field,
      message,
    }),
  };
  return createMockResponse<ProblemBody>(problem, 400);
}

/**
 * YYYYMM 書式の妥当性検証（月レンジ 01-12 のみチェック、api-server の TargetMonth と同等）。
 */
function isValidTargetMonth(value: string): boolean {
  if (!/^\d{6}$/.test(value)) return false;
  const month = Number.parseInt(value.slice(4, 6), 10);
  return month >= 1 && month <= 12;
}

/**
 * リクエスト値の undefined を null に正規化する（`??` で明示的に統一）。
 * eslint(eqeqeq) に反しない `=== null` / `!== null` チェックのため。
 */
function normalizeRequest(request: AttributionUpdateApiRequest): {
  caseId: string | null;
  employeeId: string | null;
  targetMonth: string | null;
} {
  return {
    caseId: request.caseId ?? null,
    employeeId: request.employeeId ?? null,
    targetMonth: request.targetMonth ?? null,
  };
}

/**
 * documentTypePublicId=null の未分類化リクエスト分岐。
 * 他 unit 用の値が指定されていた場合は 400 を返す（api-server と同じ排他制約）。
 */
function buildEmptyAttributionOrError(request: AttributionUpdateApiRequest):
  | {
      ok: true;
      attribution: EvidenceFileAttributionApiResponse;
    }
  | { ok: false; response: AttributionErrorResponse } {
  const { caseId, employeeId, targetMonth } = normalizeRequest(request);
  if (caseId !== null) {
    return {
      ok: false,
      response: createAttributionValidationResponse(
        'caseId',
        'caseId must be null when documentTypeId is null (未分類化)',
      ),
    };
  }
  if (employeeId !== null) {
    return {
      ok: false,
      response: createAttributionValidationResponse(
        'employeeId',
        'employeeId must be null when documentTypeId is null (未分類化)',
      ),
    };
  }
  if (targetMonth !== null) {
    return {
      ok: false,
      response: createAttributionValidationResponse(
        'targetMonth',
        'targetMonth must be null when documentTypeId is null (未分類化)',
      ),
    };
  }
  return {
    ok: true,
    attribution: {
      documentTypeCode: null,
      documentTypePublicId: null,
      attributionUnit: null,
      caseId: null,
      employeeId: null,
      targetMonth: null,
    },
  };
}

/** validate 関数が返すエラーレスポンスの共通型（400 ProblemDetail または 404 message body） */
type AttributionErrorResponse = Observable<
  HttpResponse<ProblemBody | { message: string }>
>;

/**
 * attributionUnit=case のリクエスト検証。
 * caseId 必須 + 他 unit 値は null + 同 project の Case が実在すること。
 */
function validateCaseAttribution(
  projectId: string,
  request: AttributionUpdateApiRequest,
):
  | { ok: true; caseId: string }
  | { ok: false; response: AttributionErrorResponse } {
  const { caseId, employeeId, targetMonth } = normalizeRequest(request);
  if (caseId === null) {
    return {
      ok: false,
      response: createAttributionValidationResponse(
        'caseId',
        'caseId is required for attributionUnit=case',
      ),
    };
  }
  if (employeeId !== null) {
    return {
      ok: false,
      response: createAttributionValidationResponse(
        'employeeId',
        'employeeId must be null for attributionUnit=case',
      ),
    };
  }
  if (targetMonth !== null) {
    return {
      ok: false,
      response: createAttributionValidationResponse(
        'targetMonth',
        'targetMonth must be null for attributionUnit=case',
      ),
    };
  }
  // Case の存在確認（同 project スコープ）
  const caseItem = findCaseById(projectId, caseId);
  if (!caseItem) {
    return {
      ok: false,
      response: createMockErrorResponse(
        `Case with publicId ${caseId} not found`,
        404,
      ),
    };
  }
  return { ok: true, caseId };
}

/**
 * attributionUnit=employee のリクエスト検証。
 */
function validateEmployeeAttribution(
  request: AttributionUpdateApiRequest,
):
  | { ok: true; employeeId: string }
  | { ok: false; response: AttributionErrorResponse } {
  const { caseId, employeeId, targetMonth } = normalizeRequest(request);
  if (employeeId === null) {
    return {
      ok: false,
      response: createAttributionValidationResponse(
        'employeeId',
        'employeeId is required for attributionUnit=employee',
      ),
    };
  }
  if (caseId !== null) {
    return {
      ok: false,
      response: createAttributionValidationResponse(
        'caseId',
        'caseId must be null for attributionUnit=employee',
      ),
    };
  }
  if (targetMonth !== null) {
    return {
      ok: false,
      response: createAttributionValidationResponse(
        'targetMonth',
        'targetMonth must be null for attributionUnit=employee',
      ),
    };
  }
  return { ok: true, employeeId };
}

/**
 * attributionUnit=month のリクエスト検証。YYYYMM 書式チェックを含む。
 */
function validateMonthAttribution(
  request: AttributionUpdateApiRequest,
):
  | { ok: true; targetMonth: string }
  | { ok: false; response: AttributionErrorResponse } {
  const { caseId, employeeId, targetMonth } = normalizeRequest(request);
  if (targetMonth === null) {
    return {
      ok: false,
      response: createAttributionValidationResponse(
        'targetMonth',
        'targetMonth is required for attributionUnit=month',
      ),
    };
  }
  if (!isValidTargetMonth(targetMonth)) {
    return {
      ok: false,
      response: createAttributionValidationResponse(
        'targetMonth',
        'targetMonth must be a valid YYYYMM value for attributionUnit=month',
      ),
    };
  }
  if (caseId !== null) {
    return {
      ok: false,
      response: createAttributionValidationResponse(
        'caseId',
        'caseId must be null for attributionUnit=month',
      ),
    };
  }
  if (employeeId !== null) {
    return {
      ok: false,
      response: createAttributionValidationResponse(
        'employeeId',
        'employeeId must be null for attributionUnit=month',
      ),
    };
  }
  return { ok: true, targetMonth };
}

/**
 * attributionUnit=project のリクエスト検証（他 unit 値がすべて null であること）。
 */
function validateProjectAttribution(
  request: AttributionUpdateApiRequest,
): { ok: true } | { ok: false; response: AttributionErrorResponse } {
  const { caseId, employeeId, targetMonth } = normalizeRequest(request);
  if (caseId !== null) {
    return {
      ok: false,
      response: createAttributionValidationResponse(
        'caseId',
        'caseId must be null for attributionUnit=project',
      ),
    };
  }
  if (employeeId !== null) {
    return {
      ok: false,
      response: createAttributionValidationResponse(
        'employeeId',
        'employeeId must be null for attributionUnit=project',
      ),
    };
  }
  if (targetMonth !== null) {
    return {
      ok: false,
      response: createAttributionValidationResponse(
        'targetMonth',
        'targetMonth must be null for attributionUnit=project',
      ),
    };
  }
  return { ok: true };
}

/**
 * documentTypePublicId から DocumentType を lookup し、attributionUnit に応じた
 * バリデーションを実行して attribution を組み立てる。
 */
function buildAttributionForDocumentType(
  projectId: string,
  documentTypePublicId: string,
  request: AttributionUpdateApiRequest,
):
  | { ok: true; attribution: EvidenceFileAttributionApiResponse }
  | {
      ok: false;
      response: Observable<HttpResponse<ProblemBody | { message: string }>>;
    } {
  const documentType = findTenantDocumentTypeByPublicId(documentTypePublicId);
  if (!documentType) {
    return {
      ok: false,
      response: createMockErrorResponse(
        `DocumentType with publicId ${documentTypePublicId} not found`,
        404,
      ),
    };
  }

  const attributionUnit: AttributionUnit = documentType.attributionUnit;
  const base = {
    documentTypeCode: documentType.code,
    documentTypePublicId: documentType.publicId,
    attributionUnit,
  } as const;

  switch (attributionUnit) {
    case 'case': {
      const validated = validateCaseAttribution(projectId, request);
      if (!validated.ok) return validated;
      return {
        ok: true,
        attribution: {
          ...base,
          caseId: validated.caseId,
          employeeId: null,
          targetMonth: null,
        },
      };
    }
    case 'employee': {
      const validated = validateEmployeeAttribution(request);
      if (!validated.ok) return validated;
      return {
        ok: true,
        attribution: {
          ...base,
          caseId: null,
          employeeId: validated.employeeId,
          targetMonth: null,
        },
      };
    }
    case 'month': {
      const validated = validateMonthAttribution(request);
      if (!validated.ok) return validated;
      return {
        ok: true,
        attribution: {
          ...base,
          caseId: null,
          employeeId: null,
          targetMonth: validated.targetMonth,
        },
      };
    }
    case 'project': {
      const validated = validateProjectAttribution(request);
      if (!validated.ok) return validated;
      return {
        ok: true,
        attribution: {
          ...base,
          caseId: null,
          employeeId: null,
          targetMonth: null,
        },
      };
    }
    default: {
      // 網羅性チェック（AttributionUnit 拡張時に型エラーで気付ける）
      const _exhaustive: never = attributionUnit;
      return _exhaustive;
    }
  }
}

/**
 * PATCH /api/v0/tenants/:tenantId/projects/:projectId/evidence-files/:evidenceFileId
 * EvidenceFile の attribution（書類種別紐付け）を訂正する（Issue #443/#447）。
 *
 * リクエスト:
 *   - documentTypePublicId === null: 未分類化（全 attribution を null に）
 *   - documentTypePublicId !== null: DocumentType を lookup し attributionUnit を取得、
 *     unit ごとに必須/排他制約を検証してから attribution を更新する
 *
 * レスポンス:
 *   - 200: 更新後の EvidenceFile
 *   - 400: 排他制約違反（ProblemDetail の detail に InvalidAttributionReassignmentError を JSON 埋込）
 *   - 404: EvidenceFile / DocumentType / Case が見つからない
 */
function handleUpdateEvidenceFileAttribution(
  projectId: string,
  evidenceFileId: string,
  request: AttributionUpdateApiRequest,
): Observable<HttpResponse<unknown>> {
  // EvidenceFile の存在確認
  const file = findEvidenceFileById(projectId, evidenceFileId);
  if (!file) {
    return createMockErrorResponse(
      `EvidenceFile with publicId ${evidenceFileId} not found`,
      404,
    );
  }

  // documentTypePublicId === null → 未分類化
  if (request.documentTypePublicId === null) {
    const result = buildEmptyAttributionOrError(request);
    if (!result.ok) return result.response;
    const updated = updateEvidenceFileAttribution(
      projectId,
      evidenceFileId,
      result.attribution,
    );
    if (!updated) {
      return createMockErrorResponse('ファイルの更新に失敗しました', 500);
    }
    return createMockResponse<EvidenceFileApiResponse>(updated.evidenceFile);
  }

  // documentTypePublicId !== null → DocumentType を lookup し attributionUnit 分岐
  const built = buildAttributionForDocumentType(
    projectId,
    request.documentTypePublicId,
    request,
  );
  if (!built.ok) return built.response;

  const updated = updateEvidenceFileAttribution(
    projectId,
    evidenceFileId,
    built.attribution,
  );
  if (!updated) {
    return createMockErrorResponse('ファイルの更新に失敗しました', 500);
  }
  return createMockResponse<EvidenceFileApiResponse>(updated.evidenceFile);
}
